import { UIError, SnackbarType } from 'common/interfaces';
import UILogger from 'common/utils/logger';
import { useAppDispatch } from 'setup/store';
import { useAppActions } from './app-actions';
import {
  DayReportEntryType,
  IDayReport,
  IDayReportLineItem,
  IDayReportVoidLineItem,
} from 'common/interfaces/iDayReports';
import { DayReportActions } from 'reducers/day-report-reducer';
import {
  getDayReportEntry,
  getDayReportsForStore,
  getDayReportById,
  postDeleteDayReport,
  postUpdateDaySalesEntry,
  postUpsertDayReport,
  postSubmitDayReport,
  postUpdateDayVoidEntry,
  postDeleteDayVoidEntry,
  postDayReportApproval,
  postReopenDayReport,
  postDayReportReview,
  getDayReportReviews,
  postDeleteUserDayReport,
  postChangeDayReportDate,
} from 'services/day-report-service';
import { IShiftReportReview, SalesEntryType } from 'common/interfaces/ishiftReport';
import { getAttachmentTypeFromDayReportEntryType, getAttachmentTypeFromSalesType } from 'common/utils/sales-utils';
import { getDayReportAttachments } from 'services/sales-entry-service';
import { UpdateSalesEntryActions } from 'reducers/sales/update-sales-reducer';
import { ShiftReportStatusEnum } from 'common/constants';
import { ShiftReportActions } from 'reducers/shift-report-reducer';
import { ErrorCodes } from 'common/constants/errors';
import { useTranslation } from 'react-i18next';

export const useDayReportEntryActions = () => {
  const dispatch = useAppDispatch();
  const { showSnackBar } = useAppActions();
  const { t } = useTranslation();

  const setDataLoading = async (isLoading: boolean, type: DayReportEntryType) => {
    dispatch(DayReportActions.setDataLoading({ type, isLoading }));
  };
  const setLoading = async (isLoading: boolean) => {
    dispatch(DayReportActions.setLoading(isLoading));
  };

  const resetContext = async () => {
    dispatch(DayReportActions.resetContext());
  };

  const loadDayReports = async (
    storeId: number,
    startDate: string,
    endDate: string,
    pageNo: number,
    pageSize: number,
  ) => {
    setLoading(true);
    const resp = await getDayReportsForStore(storeId, startDate, endDate, pageNo, pageSize);
    console.log('loadDayReports.....', resp?.data);
    if (resp.status.success) {
      dispatch(
        DayReportActions.setReports({
          data: resp.data,
          append: false,
        }),
      );
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadDayReports') || '');
    }
    setLoading(false);
  };

  const submitDayReport = async (reportId: number) => {
    setLoading(true);
    dispatch(DayReportActions.setError(null));

    let resp = await postSubmitDayReport(reportId);

    console.log('submitDayReport.....', resp);
    setLoading(false);
    if (resp && resp.status.success) {
      dispatch(DayReportActions.setDataUpdated({ type: 'report', isUpdated: true }));
      return true;
    } else {
      dispatch(DayReportActions.setDataUpdated({ type: 'report', isUpdated: false }));
      const msg = resp?.status?.msg || t('error.submitDayReport') || '';
      console.log('DayReportEntryActions. setError.....', msg);
      dispatch(DayReportActions.setError({ code: '', msg }));
    }
    return false;
  };

  const updateDayReportEntry = async function <T>(type: DayReportEntryType, reportId: number, payload: T[]) {
    setLoading(true);
    dispatch(DayReportActions.setError(null));
    resetUpdatedData();

    let resp = await postUpdateDaySalesEntry<IDayReportLineItem[]>(reportId, type, payload as IDayReportLineItem[]);

    console.log('updateSalesEntry.....', resp);
    setLoading(false);
    if (resp && resp.status.success) {
      dispatch(DayReportActions.setDataUpdated({ type, isUpdated: true }));
      return true;
    } else {
      dispatch(DayReportActions.setDataUpdated({ type, isUpdated: false }));
      const msg = resp?.status?.msg || t('error.updateSalesEntry') || '';
      console.log('DayReportEntryActions. setError.....', msg);
      dispatch(DayReportActions.setError({ code: '', msg }));
    }
    return false;
  };

  const updateDayReportVoidEntry = async function (reportId: number, payload: IDayReportVoidLineItem) {
    setLoading(true);
    dispatch(DayReportActions.setError(null));
    resetUpdatedData();

    let resp = await postUpdateDayVoidEntry(reportId, payload);
    console.log('updateVoidEntry.....', resp);
    setLoading(false);
    if (resp && resp.status.success) {
      dispatch(DayReportActions.setDataUpdated({ type: 'voids', isUpdated: true }));
      return true;
    } else {
      dispatch(DayReportActions.setDataUpdated({ type: 'voids', isUpdated: false }));
      const msg = resp?.status?.msg || t('error.updateSalesEntry') || '';
      console.log('DayReportEntryActions. setError.....', msg);
      dispatch(DayReportActions.setError({ code: '', msg }));
    }
    return false;
  };

  const deleteDayReportVoidEntry = async function (reportId: number, voidId: number) {
    setLoading(true);
    dispatch(DayReportActions.setError(null));
    let resp = await postDeleteDayVoidEntry(reportId, voidId);
    console.log('deleteDayReportVoidEntry.....', resp);
    setLoading(false);
    if (resp && resp.status) {
      return true;
    } else {
      const msg = resp?.status?.msg || t('error.updateSalesEntry') || '';
      console.log('deleteDayReportVoidEntry. setError.....', msg);
      dispatch(DayReportActions.setError({ code: '', msg }));
    }
    return false;
  };

  async function loadDayReportEntry<T>(type: DayReportEntryType, reportId: number) {
    setDataLoading(true, type);
    let resp = await getDayReportEntry<T>(reportId, type);
    setDataLoading(false, type);
    if (resp?.status?.success) {
      const attType = getAttachmentTypeFromDayReportEntryType(type);
      if (attType) {
        dispatch(
          UpdateSalesEntryActions.setAttachments({
            type: attType,
            attachments: resp.attachments,
          }),
        );
      } else {
        UILogger.error('could not set attachment as no attType');
      }
      return resp.data as T[];
    } else {
      UILogger.error('Could not load day report entry details. type: ', type);
      // showSnackBar(SnackbarType.ERROR, 'Could not load details.');
    }
  }

  const deleteReport = async (reportId: number) => {
    setLoading(true);
    dispatch(DayReportActions.setError(null));
    const resp = await postDeleteDayReport(reportId);
    console.log('delete day report resp.....', resp);
    if (resp?.status?.success) {
      setLoading(false);
      showSnackBar(SnackbarType.SUCCESS, t('success.delete'));
      return resp.status.success;
    } else {
      const msg = resp?.status?.msg || t('error.delete') || '';
      dispatch(DayReportActions.setError({ code: '', msg }));
      showSnackBar(SnackbarType.ERROR, msg);
    }
    return false;
  };

  const setOngoingDayReport = (dayReport: IDayReport | undefined) => {
    dispatch(DayReportActions.setOngoingReport(dayReport));
  };

  const createNewDayReport = async (
    storeId: number,
    userId: number,
    reportDate: string,
    confirmed: 'confirmed' | 'validate' = 'validate',
  ) => {
    setLoading(true);
    const resp = await postUpsertDayReport(
      {
        storeId,
        userId,
        reportDate,
      } as IDayReport,
      confirmed,
    );
    setLoading(false);
    console.log('resp.data.status::>>', resp.status);
    if (resp.status.success && resp.data) {
      const isReadOnly = resp.data.status !== ShiftReportStatusEnum.DRAFT;
      console.log('resp.data.status::', resp.data.status);

      dispatch(DayReportActions.setOngoingReport({ ...resp.data, isReadOnly: false }));
      return resp.data;
    } else {
      if (resp.status?.code === ErrorCodes.INSUFFICIENT_DATA) {
        console.log('resp.data.error::', resp.status);
        setError({ code: ErrorCodes.INSUFFICIENT_DATA, msg: resp.status.msg });
      } else {
        showSnackBar(SnackbarType.ERROR, resp.status.msg || t('error.initDayReport'));
      }
    }
  };
  const loadDayReportById = async (reportId: number) => {
    setDataLoading(false, 'report');
    dispatch(DayReportActions.setError(null));
    const resp = await getDayReportById(reportId);
    setDataLoading(false, 'report');
    if (resp.status.success && resp.data) {
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp.status.msg || t('error.loadDayReport'));
    }
  };

  const resetUpdatedData = () => {
    dispatch(DayReportActions.resetDataUpdated());
  };

  const loadDayReportAttachments = async (type: SalesEntryType, reportId: number) => {
    const attType = getAttachmentTypeFromSalesType(type);
    if (attType) {
      setLoading(true);
      const resp = await getDayReportAttachments(attType, reportId);
      UILogger.log('loadDayReportAttachments: resp', resp.data);
      setLoading(false);
      dispatch(
        UpdateSalesEntryActions.setAttachments({
          type,
          attachments: resp.data || [],
        }),
      );
    } else {
      showSnackBar(SnackbarType.ERROR, 'Invalid attachment type');
    }
  };

  const loadDayReportReviews = async (reportId: number) => {
    setLoading(true);
    const resp = await getDayReportReviews(reportId);
    console.log('loadDayReportReviews.....', resp?.data);
    if (resp.status.success) {
      dispatch(ShiftReportActions.setShiftReportReviews(resp.data));
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadReviews') || '');
    }
    setLoading(false);
  };

  const saveDayReportReviews = async (review: IShiftReportReview) => {
    setLoading(true);
    const resp = await postDayReportReview(review);
    console.log('saveDayReportReviews.....', resp?.data);
    if (resp.status.success && resp.data) {
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadReviews') || '');
    }
    setLoading(false);
  };

  const reopenDayReportForModification = async (report: IDayReport) => {
    setLoading(true);
    const resp = await postReopenDayReport(report.id);
    console.log('reopenDayReportForModification. respo.....', resp?.data);
    if (resp.status.success && resp.data) {
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.reopenReport') || '');
    }
    setLoading(false);
  };

  const approveDayReport = async (reportId: number) => {
    setLoading(true);
    const resp = await postDayReportApproval(reportId);
    setLoading(false);
    console.log('approveDayReport. resp.....', resp?.data);
    if (resp.status.success) {
      return true;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.approveReport') || '');
    }
  };
  const setError = async (error: UIError | null) => {
    dispatch(DayReportActions.setError(error));
  };

  const changeDayReportDate = async (reportId: number, newReportDate: string) => {
    setLoading(true);
    const resp = await postChangeDayReportDate(reportId, newReportDate);
    setLoading(false);
    console.log('changeDayReportDate. resp.....', resp?.data);
    if (resp.status.success) {
      showSnackBar(SnackbarType.SUCCESS, t('success.updated'));
      return true;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.updateData') || '');
    }
  };

  const deleteUserDayReport = async (reportId: number) => {
    setLoading(true);
    dispatch(DayReportActions.setError(null));
    const resp = await postDeleteUserDayReport(reportId);
    setLoading(false);
    console.log('delete user day report resp.....', resp);
    if (resp?.status?.success) {
      showSnackBar(SnackbarType.SUCCESS, t('success.delete'));
      return true;
    } else {
      const msg = resp?.status?.msg || t('error.delete') || '';
      dispatch(DayReportActions.setError({ code: '', msg }));
      showSnackBar(SnackbarType.ERROR, msg);
    }
    return false;
  };

  return {
    setLoading,
    resetContext,
    updateDayReportEntry,
    loadDayReportEntry,
    deleteReport,
    setOngoingDayReport,
    createNewDayReport,
    loadDayReports,
    loadDayReportById,
    submitDayReport,
    resetUpdatedData,
    loadDayReportAttachments,
    updateDayReportVoidEntry,
    deleteDayReportVoidEntry,
    approveDayReport,
    reopenDayReportForModification,
    saveDayReportReviews,
    loadDayReportReviews,
    setError,
    changeDayReportDate,
    deleteUserDayReport,
  };
};
